import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  SegmentedControl,
} from '@mlm/ui';
import {
  fetchReport,
  getTenantFeatures,
  type ReportParams,
  type ReportResult,
} from '../../lib/api';
import { downloadReport, type ReportExportMeta } from '../../lib/report-export';
import { ReportExportButtons } from '../../components/report-export-buttons';
import { Money, PageHeader } from './_shared';

/**
 * Financial statements — LIVE (Phase 4 lean GL). Every figure is a sum over
 * posted journal lines; the pages render exactly what the poster booked.
 * TB/BS are all-history snapshots cut at an as-of date; the Income Statement
 * is windowed, with presets that follow the tenant's fiscal-year start month
 * (no formal close — the Balance Sheet's equity split is derived).
 */

type Tab = 'tb' | 'is' | 'bs';

const peso = (v: string) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const todayStr = () => new Date().toLocaleDateString('en-CA');

function fyStartFor(day: string, fyMonth: number): string {
  const d = new Date(`${day}T00:00:00`);
  const year = d.getMonth() + 1 >= fyMonth ? d.getFullYear() : d.getFullYear() - 1;
  return `${year}-${String(fyMonth).padStart(2, '0')}-01`;
}

export function StatementsPage() {
  const [tab, setTab] = useState<Tab>('tb');
  const [asOfDay, setAsOfDay] = useState(todayStr());
  const [fromDay, setFromDay] = useState(() => todayStr().slice(0, 8) + '01');
  const [toDay, setToDay] = useState(todayStr());

  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const fyMonth = featuresQ.data?.accounting_fiscal_year_start_month ?? 1;

  // Send the picker's day AS a day (audit F23). Converting it to an instant here
  // did so in the BROWSER's timezone, so a UTC or US admin asking for "as of
  // Aug 26" actually cut the books at Manila Aug 27 and pulled a day of entries
  // into the wrong period. `posting_date` is already a tenant-local date, so the
  // server compares the two directly.
  const params: ReportParams = useMemo(() => {
    if (tab === 'is') {
      return { from: fromDay, to: toDay };
    }
    return { asOf: asOfDay };
  }, [tab, asOfDay, fromDay, toDay]);

  const reportKey =
    tab === 'tb' ? 'trial-balance' : tab === 'is' ? 'income-statement' : 'balance-sheet';
  const reportQ = useQuery({
    queryKey: ['gl-statement', reportKey, params],
    queryFn: () => fetchReport(reportKey, params),
  });

  const applyPreset = (preset: 'month' | 'lastMonth' | 'fy' | 'ytd') => {
    const today = todayStr();
    if (preset === 'month') {
      setFromDay(today.slice(0, 8) + '01');
      setToDay(today);
    } else if (preset === 'lastMonth') {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0);
      setFromDay(first.toLocaleDateString('en-CA'));
      setToDay(last.toLocaleDateString('en-CA'));
    } else if (preset === 'fy') {
      setFromDay(fyStartFor(today, fyMonth));
      setToDay(today);
    } else {
      setFromDay(`${today.slice(0, 4)}-01-01`);
      setToDay(today);
    }
  };

  const inBalance = reportQ.data?.summary?.find((s) => s.label === 'In balance');

  const statementTitle =
    tab === 'tb' ? 'Trial Balance' : tab === 'is' ? 'Income Statement' : 'Balance Sheet';
  // The as-of / period line is the whole meaning of a statement — never let a
  // downloaded file be ambiguous about which cut it is.
  const exportMeta: ReportExportMeta = {
    title: statementTitle,
    sheetName: statementTitle,
    subtitle: [tab === 'is' ? `Period: ${fromDay} → ${toDay}` : `As of: ${asOfDay}`],
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Financial Statements"
        subtitle="Live, from the general ledger — Trial Balance and Balance Sheet as-of a date; Income Statement over a period."
        actions={
          <ReportExportButtons
            result={reportQ.data}
            meta={exportMeta}
            onCsv={() =>
              downloadReport(reportKey, params, 'csv', { ...exportMeta, result: reportQ.data })
            }
          />
        }
      />

      <div className="flex flex-wrap items-end gap-4">
        <SegmentedControl
          ariaLabel="Statement"
          value={tab}
          onChange={(v) => setTab(v)}
          options={[
            { value: 'tb' as const, label: 'Trial Balance' },
            { value: 'is' as const, label: 'Income Statement' },
            { value: 'bs' as const, label: 'Balance Sheet' },
          ]}
        />
        {tab === 'is' ? (
          <>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input
                type="date"
                className="h-9"
                value={fromDay}
                onChange={(e) => setFromDay(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input
                type="date"
                className="h-9"
                value={toDay}
                onChange={(e) => setToDay(e.target.value)}
              />
            </div>
            <div className="flex gap-1 pb-0.5">
              <Button size="sm" variant="outline" onClick={() => applyPreset('month')}>
                This month
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('lastMonth')}>
                Last month
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('fy')}>
                Fiscal year
              </Button>
              <Button size="sm" variant="outline" onClick={() => applyPreset('ytd')}>
                YTD
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">As of</label>
            <Input
              type="date"
              className="h-9"
              value={asOfDay}
              onChange={(e) => setAsOfDay(e.target.value)}
            />
          </div>
        )}
        {inBalance ? (
          <div className="pb-1">
            <Badge variant={inBalance.value === 'Yes' ? 'success' : 'destructive'}>
              {inBalance.value === 'Yes' ? 'In balance' : inBalance.value}
            </Badge>
          </div>
        ) : null}
      </div>

      {reportQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !reportQ.data || reportQ.data.rows.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Nothing posted in this range yet. Entries appear as the poster books documents.
          </CardContent>
        </Card>
      ) : tab === 'tb' ? (
        <TrialBalanceTable result={reportQ.data} />
      ) : (
        <SectionedStatement result={reportQ.data} />
      )}

      {reportQ.data?.summary ? (
        <Card>
          <CardContent className="flex flex-wrap gap-x-8 gap-y-2 pt-4">
            {reportQ.data.summary
              .filter((s) => s.label !== 'In balance')
              .map((s) => (
                <div key={s.label}>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {s.label}
                  </p>
                  <Money>{peso(s.value)}</Money>
                </div>
              ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function TrialBalanceTable({ result }: { result: ReportResult }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-24 px-4 py-2">Code</th>
                <th className="px-4 py-2">Account</th>
                <th className="w-40 px-4 py-2 text-right">Debit</th>
                <th className="w-40 px-4 py-2 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-xs">{String(r.code ?? '')}</td>
                  <td className="px-4 py-2">{String(r.account ?? '')}</td>
                  <td className="px-4 py-2 text-right">
                    <Money>{r.debit ? peso(String(r.debit)) : ''}</Money>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Money>{r.credit ? peso(String(r.credit)) : ''}</Money>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionedStatement({ result }: { result: ReportResult }) {
  const sections: Array<{ name: string; rows: typeof result.rows }> = [];
  for (const row of result.rows) {
    const name = String(row.section ?? '');
    const bucket = sections.find((s) => s.name === name);
    if (bucket) bucket.rows.push(row);
    else sections.push({ name, rows: [row] });
  }
  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <Card key={section.name}>
          <CardHeader>
            <CardTitle>{section.name}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {section.rows.map((r, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="w-24 px-4 py-2 font-mono text-xs">{String(r.code ?? '')}</td>
                    <td className="px-4 py-2">{String(r.account ?? '')}</td>
                    <td className="w-44 px-4 py-2 text-right">
                      <Money>{peso(String(r.amount ?? '0'))}</Money>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
